import { Inject, Injectable, InjectionToken } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { OAuthService, OAuthSuccessEvent } from 'angular-oauth2-oidc';
import { Observable } from 'rxjs';
import { filter, map, take } from 'rxjs/operators';

import { APP_STORAGE } from '~/app.token';
import { UserService } from '~/auth/services/user/user.service';
import { ACTIVITIES_BASE_URL } from '~/core/core.module';
import { UnleashService } from '~/toggles/services/unleash.service';

import { AbstractAuthProvider } from './abstract-auth.provider';

export const CONNECT_OAUTH_SERVICE = new InjectionToken<string>(
  'CONNECT OAuth service instance'
);

export const CONNECT_ISSUER = new InjectionToken<string>('CONNECT Issuer');
export const CONNECT_CLIENT_ID = new InjectionToken<string>(
  'CONNECT client_id'
);
export const CONNECT_REDIRECT_URI = new InjectionToken<string>(
  'CONNECT redirect uri'
);
export const CONNECT_DISCOVERY_DOCUMENT = new InjectionToken<string>(
  'CONNECT discovery document'
);
export const CONNECT_LOGOUT_REDIRECTION_URI = new InjectionToken<string>(
  'CONNECT logout redirection URI'
);

@Injectable({
  providedIn: 'root',
})
export class ConnectProvider extends AbstractAuthProvider {
  constructor(
    @Inject(ACTIVITIES_BASE_URL) protected readonly activitiesUrl: string,
    @Inject(APP_STORAGE) protected readonly storage: Storage,
    @Inject(CONNECT_CLIENT_ID) protected readonly clientId: string,
    @Inject(CONNECT_DISCOVERY_DOCUMENT)
    protected readonly discoveryDocument: string,
    @Inject(CONNECT_ISSUER) protected readonly issuer: string,
    protected readonly oAuthService: OAuthService,
    @Inject(CONNECT_REDIRECT_URI) protected readonly redirectUri: string,
    @Inject(CONNECT_LOGOUT_REDIRECTION_URI)
    private readonly logoutRedirectionUrl: string,
    private readonly toggleService: UnleashService,
    protected readonly userService: UserService,
    protected readonly translateService: TranslateService
  ) {
    super(
      oAuthService,
      userService,
      {
        issuer,
        redirectUri,
        clientId,
        userinfoEndpoint: `${activitiesUrl}/me`,
        responseType: 'code',
      },

      discoveryDocument,
      storage,
      translateService
    );
  }

  protected getScopes(): Observable<string[]> {
    return this.toggleService.isInitialized().pipe(
      filter((initialized) => initialized),
      map(() => {
        return [
          'contacts',
          'contacts:write',
          'email',
          'openid',
          'optins',
          'optins:write',
          'profile',
          'profile:write',
          'sports',
          'sports:write',
          'sports_tracking_data',
          'sports_tracking_data:write',
          'account:birthdate',
          'account:gender',
          'account:identifiers',
          'account:morphology',
          'account:profile',
          'birthdate',
          'gender',
          'identifiers',
          'phone',
          'account:consent',
          'consent',
        ];
      }),
      take(1)
    );
  }

  /**
   * @see https://login-doc.decathlon.com/logout.html
   */
  public logout(): Promise<OAuthSuccessEvent> {
    // `super.logout()` removes the access token of the session storage, so we need to get it before.
    const accessToken = sessionStorage.getItem('access_token');
    // Be safe. `logout` should return a promise but it can return `undefined`.
    const promise = super.logout();

    window.location.href =
      `${this.oAuthService.logoutUrl}?id_token_hint=${accessToken}` +
      `&post_logout_redirect_uri=${this.logoutRedirectionUrl}`;

    return promise;
  }

  public tryLogin(): Promise<boolean> {
    this.oAuthService.customQueryParams = {
      ui_locales: this.translateService.currentLang,
    };

    this.oAuthService.events.subscribe((event) => {
      if (event.type === 'token_received') {
        const state = decodeURIComponent(this.oAuthService.state);

        this.processState(state);
      }
    });

    return this.oAuthService
      .tryLoginCodeFlow({
        preventClearHashAfterLogin: true,
      })
      .then(() => this.getOAuthService().hasValidAccessToken());
  }
}
